import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { database } from '../utils/database';
import supabase from '../lib/supabase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);

  const updateLocalStorageIfChanged = (newUser) => {
    const currentUser = database.getCurrentUser();
    if (JSON.stringify(currentUser) !== JSON.stringify(newUser)) {
      console.log('Updating localStorage with new user data');
      database.loginUser(newUser);
    } else {
      console.log('No changes to user data, skipping localStorage update');
    }
  };

  useEffect(() => {
    const fetchTeams = async () => {
      if (!user?.id) {
        setTeams([]);
        setTeamsLoading(false);
        return;
      }

      setTeamsLoading(true);
      try {
        const { data: teamMemberships, error: membershipError } = await supabase
          .from('team_members_a1b2c3d4e5')
          .select('team_id')
          .eq('user_id', user.id);

        if (membershipError) throw membershipError;

        if (teamMemberships && teamMemberships.length > 0) {
          const teamIds = teamMemberships.map((tm) => tm.team_id);
          const { data: userTeams, error: teamsError } = await supabase
            .from('teams_a1b2c3d4e5')
            .select('*')
            .in('id', teamIds)
            .order('name');

          if (teamsError) throw teamsError;
          setTeams(userTeams || []);
        } else {
          setTeams([]);
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
        setTeams([]);
      } finally {
        setTeamsLoading(false);
      }
    };

    fetchTeams();
  }, [user]);

  const fetchUserProfile = async (userId) => {
    try {
      setProfileLoading(true);
      console.log('Fetching user profile for:', userId);

      const { data: profile, error } = await supabase
        .from('user_profiles_a1b2c3d4e5')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Error fetching user profile:', error);
        return null;
      }

      console.log('User profile fetched:', profile);
      return profile;
    } catch (err) {
      console.error('Error in fetchUserProfile:', err);
      return null;
    } finally {
      setProfileLoading(false);
    }
  };

  const refreshUserProfile = async () => {
    if (!user?.id) {
      console.warn('Cannot refresh profile: no user ID available');
      return;
    }

    try {
      setProfileLoading(true);
      console.log('Refreshing user profile for:', user.id);

      const profile = await fetchUserProfile(user.id);
      if (profile) {
        const updatedUser = {
          ...user,
          fullName: profile.full_name || user.fullName,
          role: profile.role || 'Team Member',
          avatarUrl: profile.avatar_url || user.avatarUrl,
          language: profile.language || user.language,
          id: user.id,
          email: user.email,
        };

        console.log('User profile refreshed:', updatedUser);
        setUser(updatedUser);
        updateLocalStorageIfChanged(updatedUser);
        return updatedUser;
      }
    } catch (error) {
      console.error('Error refreshing user profile:', error);
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setLoading(true);
        const { data: { session } } = await supabase.auth.getSession();

        if (session?.user) {
          console.log('Active session found, fetching profile...', session.user);
          const profile = await fetchUserProfile(session.user.id);

          const authenticatedUser = {
            id: session.user.id,
            email: session.user.email,
            fullName: profile?.full_name || '',
            role: profile?.role || 'Team Member',
            avatarUrl: profile?.avatar_url || '',
            language: profile?.language || 'fr',
          };

          setUser(authenticatedUser);
          updateLocalStorageIfChanged(authenticatedUser);
        } else {
          console.log('No active session, checking localStorage...');
          const currentUser = database.getCurrentUser();

          if (currentUser?.id) {
            const profile = await fetchUserProfile(currentUser.id);
            const updatedUser = {
              ...currentUser,
              role: profile?.role || currentUser.role || 'Team Member',
              fullName: profile?.full_name || currentUser.fullName,
              avatarUrl: profile?.avatar_url || currentUser.avatarUrl,
              language: profile?.language || currentUser.language,
            };
            setUser(updatedUser);
            updateLocalStorageIfChanged(updatedUser);
          } else {
            setUser(null);
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setUser(null);
      } finally {
        setLoading(false);
      }

      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        console.log('Auth event:', event, 'Session:', session);

        if (event === 'SIGNED_IN' && session?.user) {
          setUser((prev) => {
            if (prev?.id === session.user.id) {
              console.log('User already authenticated, skipping update:', session.user.id);
              return prev;
            }

            console.log('Fetching profile for new sign-in:', session.user.id);
            setTimeout(async () => {
              const profile = await fetchUserProfile(session.user.id);
              const authenticatedUser = {
                id: session.user.id,
                email: session.user.email,
                fullName: profile?.full_name || '',
                role: profile?.role || 'Team Member',
                avatarUrl: profile?.avatar_url || '',
                language: profile?.language || 'fr',
              };
              setUser(authenticatedUser);
              updateLocalStorageIfChanged(authenticatedUser);
            }, 0);

            return prev;
          });
        } else if (event === 'SIGNED_OUT') {
          console.log('User signed out');
          setUser(null);
          database.logout();
        } else if (event === 'USER_UPDATED' && session?.user) {
          setTimeout(async () => {
            console.log('User updated, refreshing profile...');
            const profile = await fetchUserProfile(session.user.id);
            setUser((prev) => {
              const updatedUser = {
                ...prev,
                email: session.user.email,
                fullName: profile?.full_name || prev?.fullName,
                role: profile?.role || prev?.role || 'Team Member',
                avatarUrl: profile?.avatar_url || prev?.avatarUrl,
                language: profile?.language || prev?.language,
              };
              updateLocalStorageIfChanged(updatedUser);
              return updatedUser;
            });
          }, 0);
        }
      });

      return () => {
        subscription?.unsubscribe();
      };
    };

    initializeAuth();
  }, []);

  const login = async (email, password) => {
    try {
      console.log('Attempting login for:', email);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data?.user) {
        console.log('Authentication successful, fetching profile...');
        const profile = await fetchUserProfile(data.user.id);

        const authenticatedUser = {
          id: data.user.id,
          email: data.user.email,
          fullName: profile?.full_name || '',
          role: profile?.role || 'Team Member',
          avatarUrl: profile?.avatar_url || '',
          language: profile?.language || 'fr',
        };

        console.log('Login successful with profile:', authenticatedUser);
        setUser(authenticatedUser);
        updateLocalStorageIfChanged(authenticatedUser);
        return authenticatedUser;
      }

      throw new Error('Invalid credentials');
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      console.log('Attempting registration for:', userData.email);
      const { data, error } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
      });

      if (error) throw error;

      if (data?.user) {
        console.log('Registration successful, creating profile...');
        const { error: profileError } = await supabase
          .from('user_profiles_a1b2c3d4e5')
          .insert([
            {
              id: data.user.id,
              full_name: userData.fullName,
              role: userData.role || 'Team Member',
              language: userData.language || 'fr',
            },
          ]);

        if (profileError) throw profileError;

        const newUser = {
          id: data.user.id,
          email: userData.email,
          fullName: userData.fullName,
          role: userData.role || 'Team Member',
          language: userData.language || 'fr',
        };

        console.log('Registration complete with profile:', newUser);
        setUser(newUser);
        updateLocalStorageIfChanged(newUser);
        return newUser;
      }

      throw new Error('Registration failed');
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      console.log('Attempting logout...');
      const { error } = await supabase.auth.signOut();

      if (error) throw error;

      database.logout();
      setUser(null);
      window.location.reload();
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const updateUser = async (updates) => {
    if (!user) return null;

    try {
      console.log('Updating user profile:', updates);
      const { error } = await supabase
        .from('user_profiles_a1b2c3d4e5')
        .update({
          full_name: updates.fullName || user.fullName,
          avatar_url: updates.avatarUrl || user.avatarUrl,
          language: updates.language || user.language,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;

      const updatedUser = {
        ...user,
        fullName: updates.fullName || user.fullName,
        avatarUrl: updates.avatarUrl || user.avatarUrl,
        language: updates.language || user.language,
      };

      console.log('User profile updated:', updatedUser);
      setUser(updatedUser);
      updateLocalStorageIfChanged(updatedUser);
      return updatedUser;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  };

  const updateUserRole = async (userId, newRole) => {
    if (!user || user.role !== 'Administrator') {
      throw new Error('Permission denied: Only administrators can update roles');
    }

    try {
      console.log('Updating user role:', userId, newRole);
      const { error } = await supabase
        .from('user_profiles_a1b2c3d4e5')
        .update({
          role: newRole,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userId);

      if (error) throw error;

      if (userId === user.id) {
        console.log('Updated own role, refreshing profile...');
        await refreshUserProfile();
      }

      console.log('User role updated successfully');
      return { success: true };
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      profileLoading,
      login,
      register,
      logout,
      updateUser,
      refreshUserProfile,
      updateUserRole,
      teams,
      teamsLoading,
    }),
    [user, loading, profileLoading, teams, teamsLoading]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};